import { HttpStatusCode } from "axios";
import React, { useRef, useState } from "react";

import { authRequest } from "../../libs/request";
import { convertBirthToFullYear } from "../../libs/dateFormat";

import { KeyValue } from "../../types/keyValue";

import {
  CheckCertRequest,
  Nation,
  SendCertRequest,
  checkCert,
  checkMobileNumberDuplicate,
  sendCert,
} from "../../services/authServices";
import { PublicTermPurpose } from "../../services/etcServices";

import useAgreement from "../useAgreement";
import useSimplePopup from "../useSimplePopup";
import useFullScreenLoading from "../useFullScreenLoading";

import CommonInput from "../../components/Input/CommonInput";
import CommonButton from "../../components/Button";
import CommonSelect, { SelectOption } from "../../components/Select";
import TermsPopup from "../../components/Popup/Terms";
import RadioButtonGroup from "../../components/Input/RadioButtonGroup";

export type IdentityVerificationData = SendCertRequest & {
  authRequested: boolean;
  authnum: string;
  reqNo: string;
  ci: string;
  di: string;
};

type Props = { needDuplicate?: boolean };

export default function useIdentityVerification({
  needDuplicate = false,
}: Props): [
  {
    data: Partial<IdentityVerificationData>;
    isComplete: boolean;
    validate: () => boolean;
  },
  React.ReactNode
] {
  const [data, setData] = useState<Partial<IdentityVerificationData>>({});
  const [popup, setPopup] = useState<string>("");
  const [terms, setTerms] = useState<PublicTermPurpose | "">("");
  const telecomList: SelectOption[] = [
    { value: "skt", label: "SKT" },
    { value: "kt", label: "KT" },
    { value: "lg", label: "LG U+" },
    { value: "skt-save", label: "SKT 알뜰폰" },
    { value: "kt-save", label: "KT 알뜰폰" },
    { value: "lg-save", label: "LG U+ 알뜰폰" },
  ];
  const [
    { isComplete: isCompleteAgreement, validate: validateAgreement },
    agreementComponent,
  ] = useAgreement({
    allTitle: "본인인증 서비스 이용약관 전체 동의",
    onAllClick: () => {
      if (!data?.telco) {
        open("통신사를 선택해주세요.");
        return false;
      }
      return true;
    },
    agreementList: [
      {
        title: "개인정보 이용 동의",
        required: true,
        onClick: () => {
          if (!data?.telco) {
            open("통신사를 선택해주세요.");
            return false;
          }

          setPopup("showTerms");
          setTerms(
            `${data.telco
              ?.replace("-save", "")
              .replace("lg", "lgt")}_personal_use` as PublicTermPurpose
          );
          return true;
        },
        disabled: !data?.telco,
      },
      {
        title: "고유식별 정보처리 동의",
        required: true,
        onClick: () => {
          if (!data?.telco) {
            open("통신사를 선택해주세요.");
            return false;
          }

          setPopup("showTerms");
          setTerms(
            `${data.telco
              ?.replace("-save", "")
              .replace("lg", "lgt")}_self_verification` as PublicTermPurpose
          );
          return true;
        },
        disabled: !data?.telco,
      },
      {
        title: "서비스 이용약관 동의",
        required: true,
        onClick: () => {
          setPopup("showTerms");
          setTerms(`common_service` as PublicTermPurpose);
          return true;
        },
      },
      {
        title: "통신사 이용약관 동의",
        required: true,
        onClick: () => {
          if (!data?.telco) {
            open("통신사를 선택해주세요.");
            return false;
          }

          setPopup("showTerms");
          setTerms(
            `${data.telco
              ?.replace("-save", "")
              .replace("lg", "lgt")}_identification` as PublicTermPurpose
          );
          return true;
        },
        disabled: !data?.telco,
      },
    ],
  });
  const [isComplete, setIsComplete] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const nationItem: KeyValue[] = [
    { key: "0", value: "내국인" },
    { key: "1", value: "외국인" },
  ];
  const [selectedNation, setSelectedNation] = useState<KeyValue>();

  const [nameError, setNameError] = useState<string>("");
  const [birthError, setBirthError] = useState<string>("");
  const [telcoError, setTelcoError] = useState<string>("");
  const [mdnError, setMdnError] = useState<string>("");
  const [authnumError, setAuthnumError] = useState<string>("");
  const [nationError, setNationError] = useState<string>("");

  const nameInputRef = React.useRef<HTMLInputElement>(null);
  const birthInputRef = React.useRef<HTMLInputElement>(null);
  const genderInputRef = React.useRef<HTMLInputElement>(null);
  const mdnInputRef = React.useRef<HTMLInputElement>(null);
  const authnumInputRef = React.useRef<HTMLInputElement>(null);
  const telcoSelectRef = React.useRef<HTMLDivElement>(null);
  const agreementRef = useRef<HTMLDivElement>(null);
  const nationRef = useRef<HTMLDivElement>(null);

  const [simplePopupComponent, { open }] = useSimplePopup({});

  const identityValidate = () => {
    setNameError("");
    setBirthError("");
    setMdnError("");
    setAuthnumError("");
    setNationError("");

    if (!data?.telco?.length) {
      open("통신사를 선택해주세요.");
      telcoSelectRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return false;
    }
    if (!validateAgreement()) {
      agreementRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return false;
    }
    if (!data?.name?.length) {
      setNameError("이름을 입력해주세요.");
      nameInputRef.current?.focus();
      return false;
    }
    if (!data?.birth?.length || data?.birth?.length !== 6) {
      setBirthError("생년월일을 입력해주세요.");
      birthInputRef.current?.focus();
      return false;
    }
    if (!data?.gender?.length || data?.gender?.length !== 1) {
      setBirthError("성별을 입력해주세요.");
      genderInputRef.current?.focus();
      return false;
    }
    if (!selectedNation) {
      setNationError("회원 유형을 선택해주세요.");
      nationRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return false;
    }
    if (!data?.mdn?.length || data?.mdn?.length !== 11) {
      setMdnError("연락처를 입력해주세요.");
      mdnInputRef.current?.focus();
      return false;
    }

    return true;
  };

  const validate = () => {
    setNameError("");
    setBirthError("");
    setMdnError("");
    setAuthnumError("");

    if (!identityValidate()) return false;

    if (!data?.authRequested) {
      setMdnError("인증 요청을 해주세요.");
      mdnInputRef.current?.focus();
      return false;
    }
    if (!data?.authnum?.length || data?.authnum?.length !== 6) {
      setAuthnumError("인증번호를 입력해주세요.");
      authnumInputRef.current?.focus();
      return false;
    }
    if (!isComplete) {
      setAuthnumError("인증번호를 확인해주세요.");
      return false;
    }

    return true;
  };

  return [
    { data, isComplete: isComplete && isCompleteAgreement, validate },
    <div className="flex flex-col gap-5">
      {loadingComponent}
      <div>
        <CommonSelect
          defaultValue="통신사"
          value={data.telco}
          optionList={telecomList}
          onChange={(value) => {
            setData((prev) => ({
              ...prev,
              telco: value.value,
            }));
            setTelcoError("");
            setIsComplete(false);
          }}
          ref={telcoSelectRef}
          error={telcoError ? "" : undefined}
          wrapperClassName="!w-[130px]"
          className="h-[50px] rounded-[3px] !border-brand-neutral-15"
          optionOrder="between"
        />
        <div ref={agreementRef}>{agreementComponent}</div>
      </div>
      <div className="flex flex-col">
        <div className="py-2.5 text-sm text-brand-neutral-60">이름</div>
        <CommonInput
          placeholder="이름을 입력해주세요"
          value={data?.name ?? ""}
          onChange={(text) => {
            setData((prev) => ({ ...prev, name: text }));
            setNameError("");
          }}
          error={nameError}
          ref={nameInputRef}
        />
      </div>
      <div className="flex flex-col">
        <div className="py-2.5 text-sm text-brand-neutral-60">
          생년월일/성별
        </div>
        <div className="flex items-start">
          <div className="flex gap-2.5">
            <CommonInput
              placeholder="●●●●●●"
              value={data?.birth ?? ""}
              onChange={(text) =>
                setData((prev) => ({
                  ...prev,
                  birth: text.replace(/\D/g, "").slice(0, 6),
                }))
              }
              onKeyDown={(e) => {
                if ([".", "e", "E", "-"].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              type="number"
              pattern="[0-9]*"
              error={birthError ? "" : undefined}
              ref={birthInputRef}
            />
            <div className="h-[50px] flex items-center">-</div>
            <div className="flex gap-0.5">
              <CommonInput
                placeholder="●"
                value={data?.gender ?? ""}
                onChange={(text) =>
                  setData((prev) => ({
                    ...prev,
                    gender: text.replace(/\D/g, "").slice(0, 1),
                  }))
                }
                onKeyDown={(e) => {
                  if ([".", "e", "E", "-"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                type="number"
                pattern="[0-9]*"
                error={birthError ? "" : undefined}
                ref={genderInputRef}
              />
              <div className="h-[50px] flex items-center">●●●●●●</div>
            </div>
          </div>
        </div>
        {birthError && (
          <div className="text-brand-primary-magenta text-xs px-4 pt-1">
            *{birthError}
          </div>
        )}
      </div>
      <div className="flex flex-col">
        <div className="py-2.5 text-sm text-brand-neutral-60">회원 유형</div>
        <div className="flex items-start" ref={nationRef}>
          <RadioButtonGroup
            variantActive="select"
            items={nationItem}
            value={selectedNation as KeyValue}
            onChange={(selectItem) => {
              setSelectedNation(selectItem);
            }}
            className="w-full"
          />
        </div>
        {nationError && (
          <div className="text-brand-primary-magenta text-xs px-4 pt-1">
            *{nationError}
          </div>
        )}
      </div>
      <div>
        <div className="py-2.5 text-sm text-brand-neutral-60">휴대폰 번호</div>
        <div className="flex gap-1.5 flex-wrap">
          <div className="relative flex-1">
            <CommonInput
              placeholder="연락처를 입력해주세요"
              type="number"
              value={data?.mdn ?? ""}
              onChange={(text: string) => {
                setData((prev) => ({
                  ...prev,
                  mdn: text.replace(/\D/g, "").slice(0, 11),
                }));
                setMdnError("");
                setIsComplete(false);
              }}
              onKeyDown={(e) => {
                if ([".", "e", "E", "-"].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              error={mdnError ? "" : undefined}
              className="pr-20 w-full"
              pattern="[0-9]*"
              postfix={{
                content: (
                  <CommonButton
                    variant={
                      (data?.mdn?.length ?? 0) > 8 ? "primary" : "tertiary-s"
                    }
                    className="absolute right-1 top-1/2 -translate-y-1/2 whitespace-nowrap text-[13px] leading-[17px] rounded-[3px] h-[32px] px-2.5 py-[11px]"
                    onClick={async () => {
                      if (data.authRequested === true) {
                        setIsComplete(false);
                        setData((prev) => ({
                          ...prev,
                          authnum: "",
                        }));
                      }

                      if (!identityValidate()) return;

                      setIsLoading(true);

                      if (!needDuplicate) {
                        try {
                          const res = await checkMobileNumberDuplicate(
                            data.mdn ?? ""
                          );
                          if (
                            res.status === HttpStatusCode.Ok &&
                            res.data.isDuplicated === false
                          ) {
                            // nothing to do
                            setMdnError("");
                          } else {
                            setMdnError("이미 가입된 휴대폰 번호입니다.");
                            authnumInputRef.current?.focus();
                            setIsLoading(false);
                            return;
                          }
                        } catch (e: any) {
                          setMdnError("이미 가입된 휴대폰 번호입니다.");
                          authnumInputRef.current?.focus();
                          setIsLoading(false);
                          return;
                        }
                      }

                      sendCert({
                        ...(data as SendCertRequest),
                        nation: (selectedNation?.key as Nation) ?? "0",
                        telco: `0${
                          telecomList.findIndex(
                            (item) => item.value === data.telco
                          ) + 1
                        }`,
                        birth: convertBirthToFullYear(data.birth as string),
                      })
                        .then((res) => {
                          if (
                            res.status === HttpStatusCode.Ok &&
                            res.data.resultCd === "0000"
                          ) {
                            setData((prev) => ({
                              ...prev,
                              reqNo: res.data.reqNo,
                            }));
                            setMdnError("");
                            open("인증 번호가 발송되었습니다.");
                            setData((prev) => ({
                              ...prev,
                              authRequested: true,
                            }));
                            authnumInputRef.current?.focus();
                          } else {
                            if (res.data.resultCd === "0011") {
                              setMdnError("통신사에 없는 전화번호예요.");
                            } else if (res.data.resultCd === "0012") {
                              setMdnError("생년월일과 성별을 확인해주세요.");
                            } else if (res.data.resultCd === "0024") {
                              setMdnError(
                                "휴대폰 명의자 이름을 다시 확인해주세요."
                              );
                            } else if (res.data.resultCd === "0025") {
                              setMdnError(
                                "법인 명의 휴대폰 번호는 사용할 수 없습니다."
                              );
                            } else if (res.data.resultCd === "0043") {
                              setMdnError(
                                "유효하지 않은 인증번호예요.\n인증번호를 다시 확인해주세요."
                              );
                            } else if (res.data.resultCd === "0097") {
                              setMdnError("입력한 정보를 다시 확인해주세요.");
                            } else if (res.data.resultCd === "0098") {
                              setMdnError(
                                "통신사와의 통신이 원할하지 않아요.\n잠시후 다시 시도해주세요."
                              );
                            } else if (res.data.resultCd === "0099") {
                              setMdnError(
                                "서버와의 통신이 원할하지 않아요.\n잠시후 다시 시도해주세요."
                              );
                            } else {
                              console.log("미처리된 에러", res.data.resultCd);
                              setMdnError("개인 정보를 확인해주세요.");
                            }
                          }
                        })
                        .catch((e: any) => {
                          setMdnError("개인 정보를 확인해주세요.");
                        })
                        .finally(() => setIsLoading(false));
                    }}
                  >
                    {data?.authRequested === true ? "다시 전송" : "인증요청"}
                  </CommonButton>
                ),
              }}
            />
          </div>
        </div>
        {(telcoError || mdnError) && (
          <div className="text-brand-primary-magenta text-xs px-4 pt-1">
            *{telcoError || mdnError}
          </div>
        )}
      </div>
      {data?.authRequested && (
        <div className="flex flex-col">
          <div className="py-2.5 text-sm text-brand-neutral-60">
            인증번호 입력
          </div>
          <div className="relative">
            <CommonInput
              placeholder="인증번호를 입력해주세요"
              type="number"
              pattern="[0-9]*"
              value={data.authnum ?? ""}
              onChange={(text: string) => {
                setData((prev) => ({
                  ...prev,
                  authnum: text.replace(/\D/g, "").slice(0, 6),
                }));
                setAuthnumError("");
                setIsComplete(false);
              }}
              onKeyDown={(e) => {
                if ([".", "e", "E", "-"].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              disabled={isComplete}
              postfix={{
                content: (
                  <CommonButton
                    variant={
                      (data?.authnum?.length ?? 0) === 6
                        ? "primary"
                        : "tertiary-s"
                    }
                    className="absolute right-1 top-1/2 -translate-y-1/2 w-[70px] whitespace-nowrap  text-[13px] leading-[17px] rounded-[3px] h-[32px] px-2.5 py-[11px]"
                    onClick={() => {
                      if (!authRequest || data.authnum?.length !== 6) {
                        setAuthnumError("인증번호를 확인해주세요.");
                        return;
                      }

                      if (!identityValidate()) return;

                      setIsLoading(true);
                      checkCert({
                        ...(data as CheckCertRequest),
                        nation: (selectedNation?.key as Nation) ?? "0",
                        telco: `0${
                          telecomList.findIndex(
                            (item) => item.value === data.telco
                          ) + 1
                        }`,
                        birth: convertBirthToFullYear(data.birth as string),
                      })
                        .then((res) => {
                          if (
                            res.status === HttpStatusCode.Ok &&
                            res.data.resultCd === "0000"
                          ) {
                            open("인증되었습니다.");
                            setAuthnumError("");
                            setData((prev) => ({
                              ...prev,
                              ci: res.data.ci,
                              di: res.data.di,
                            }));
                            setIsComplete(true);
                          } else {
                            console.log("미처리된 에러", res.data.resultCd);
                            setAuthnumError("잘못된 인증번호입니다.");
                          }
                        })
                        .catch((e) => {
                          setAuthnumError("잘못된 인증번호입니다.");
                        })
                        .finally(() => setIsLoading(false));
                    }}
                    disabled={isComplete}
                  >
                    인증하기
                  </CommonButton>
                ),
              }}
              error={authnumError}
              ref={authnumInputRef}
              autoComplete="one-time-code"
            />
          </div>
        </div>
      )}

      <TermsPopup
        popup={popup}
        onClose={() => setPopup("")}
        type={terms as PublicTermPurpose}
      />

      {simplePopupComponent}
    </div>,
  ];
}
